// ============================================================================
// Pakistan fiscal adapter (FBR — Federal Board of Revenue, Digital Invoicing).
// ----------------------------------------------------------------------------
// Same interface as the Spain adapter (see ./index.js). Pakistan sales tax is a
// straight GST on the taxable base — no recargo, no IBAE, no IRPF — so the
// per-rate math reuses lib/taxes.js with the Spanish surcharges switched off.
//
// STATUS: tax math + field rules are live. `submit` posts the FBR Digital
// Invoicing payload to the backend bridge (POST /api/fbr/submit-invoice), which
// holds the encrypted Bearer token and forwards to the FBR gateway
// (postinvoicedata / postinvoicedata_sb), returning the IRN + QR.
//
// Payload shape follows the PRAL "Technical Documentation for DI API" v1.12
// (SRO 69(I)/2025). Field ENUMERATIONS that FBR validates against controlled
// lists — saleType, uoM, sroScheduleNo/sroItemSerialNo, scenarioId — are marked
// below and must be reconciled against the client's live spec before go-live.
// ============================================================================
import { groupLinesByTaxRate, round2 } from '../taxes';
import { API_BASE } from '../api';
import { normaliseUom } from '../uom';

// Pakistan standard GST is 18%. Reduced/zero rates still come off each line's
// own tax_rate, so we don't hardcode a single rate into the math — only the
// default the editor should offer.
export const PK_STANDARD_GST = 18;

// NTN = 7 digits; STRN = 13 digits; buyer may instead give a 13-digit CNIC.
const NTN_RE  = /^\d{7}$/;
const STRN_RE = /^\d{13}$/;
const CNIC_RE = /^\d{13}$/;

// ─── PRA (Punjab Revenue Authority) e-Invoicing ─────────────────────────────
// Sales tax on SERVICES is provincial in Pakistan: a Punjab service provider
// files each invoice with PRA's Invoice Management System (IMS, run by PRAL)
// and prints the PRA invoice number + QR, the same way goods go to FBR. A PK
// account carries BOTH channels (einvoice = FBR, einvoicePra = PRA) and the
// seller connects whichever authorities apply. Payload = PRAL's "Technical
// Specification for Data Sharing through Software Fiscal Device with PRA"
// ("Web Client to post data from Cloud" flow); see docs/PRA_INVOICING.md.
export const PRA_VERIFY_URL = 'https://e.pra.punjab.gov.pk/IMSFiscalReport/SearchPOSInvoice_Report.aspx?PRAInvNo=';
export const praVerifyUrl = (number) => `${PRA_VERIFY_URL}${encodeURIComponent(number || '')}`;

// ENUM — PRA PaymentMode: 1 Cash · 2 Card · 3 Gift Voucher · 4 Loyalty Card ·
// 5 Mixed · 6 Cheque. Keyed by the canonical stored payment_method values
// (lib/paymentMethods.js). Anything else — bank transfer, wallets, an invoice
// not paid yet — goes as Cash, the spec's default; revisit if PRA adds a
// transfer code to the list.
const PRA_PAYMENT_MODES = { Efectivo: 1, Tarjeta: 2, Mixto: 5, Cheque: 6 };
export const praPaymentMode = (method) => PRA_PAYMENT_MODES[String(method || '').trim()] || 1;

// PCTCode = Pakistan Customs Tariff code, 8 digits; PRA says to send 00000000
// when there is none. It is the same number as the line's HS code (the FBR
// field), so the one box on the line serves both authorities.
export const praPctCode = (hs) => {
  const digits = String(hs || '').replace(/\D/g, '');
  return digits ? digits.slice(0, 8).padEnd(8, '0') : '00000000';
};

// "2018-11-01 00:00:00.000": the issue date, with the time the invoice was
// created when that falls on the same (local) day, else midnight.
export const praDateTime = (invoice) => {
  const date = String(invoice?.issue_date || invoice?.date || '').slice(0, 10);
  let time = '00:00:00';
  if (invoice?.created_at) {
    const c = new Date(invoice.created_at);
    const p = (n) => String(n).padStart(2, '0');
    if (!Number.isNaN(c.getTime()) && `${c.getFullYear()}-${p(c.getMonth() + 1)}-${p(c.getDate())}` === date) {
      time = `${p(c.getHours())}:${p(c.getMinutes())}:${p(c.getSeconds())}`;
    }
  }
  return `${date} ${time}.000`;
};

export const pkAdapter = {
  countryCode: 'PK',

  meta: {
    name: 'Pakistan',
    currency: 'PKR',
    currencySymbol: 'Rs',
    currencyPosition: 'before',     // "Rs 1,234.56"
    locale: 'en-PK',
    taxIdLabel: 'NTN / STRN',   // issuer (profiles.pk_ntn / pk_strn)
    clientTaxIdLabel: 'NTN / CNIC', // buyer (clients.tax_id)
    taxLabel: 'GST',            // primary tax name (editor headers/labels)
  },

  // Pakistan shows none of the Spanish surcharge controls.
  taxFeatures: {
    recargoEquiv: false,
    ibae: false,
    irpf: false,
    suplidos: false,
  },

  defaultTaxRate: PK_STANDARD_GST,

  // Per-line tax-rate options: standard GST 18% (default), plus 16% and the
  // reduced 8%/5% and zero rates. Any other rate can still be typed via the
  // editor's "custom %" option — this list is just the one-click presets.
  taxRates: [
    { value: '18', label: '18% GST' },
    { value: '16', label: '16% GST' },
    { value: '8',  label: '8% GST' },
    { value: '5',  label: '5% GST' },
    { value: '0',  label: '0% GST' },
  ],

  // GST-only totals. Reuse the per-rate aggregator with the Spanish surcharges
  // off, so `iva` here is simply the GST cuota per rate.
  computeTotals(lines, opts = {}) {
    const byRate = groupLinesByTaxRate(lines, { recargoEquiv: false, applyIbi: false });
    let subtotal = 0, gstTotal = 0;
    byRate.forEach((r) => { subtotal += round2(r.productBase ?? r.base); gstTotal += round2(r.iva); });
    subtotal = round2(subtotal);
    gstTotal = round2(gstTotal);
    return {
      byRate,
      subtotal,
      taxTotal: gstTotal,
      taxLabel: 'GST',
      extraTotals: [],
      deductions: [],
      grandTotal: round2(subtotal + gstTotal),
    };
  },

  // FBR invoices require an HS code per line in addition to the buyer id.
  requiredClientFields() {
    return ['tax_id']; // buyer NTN or CNIC
  },

  requiredLineFields() {
    return ['hs_code'];
  },

  validateTaxId(id) {
    const v = String(id || '').replace(/[\s-]/g, '');
    return NTN_RE.test(v) || STRN_RE.test(v) || CNIC_RE.test(v);
  },

  einvoice: {
    enabled: true,
    featureKey: 'fbr_invoicing',   // gated per client (see feature_management.sql)
    label: 'FBR Digital Invoicing',

    // Build the FBR Digital Invoicing request body from a document. Lines carry
    // a NET base in `total` (see lib/taxes) — GST is added per line here.
    buildPayload({ invoice, lines, profile, client }) {
      const n = (v) => Number(v || 0);
      const items = (lines || []).map((ln) => {
        const rate = n(ln.tax_rate ?? ln.taxRate);
        const base = n(ln.total);                       // net line value (excl. ST)
        const salesTax = round2(base * rate / 100);
        return {
          hsCode: ln.hs_code || '',
          productDescription: ln.description || ln.name || '',
          rate: `${rate}%`,
          uoM: normaliseUom(ln.uom),                    // ENUM — FBR UoM list (lib/uom.js)
          quantity: n(ln.quantity),
          totalValues: round2(base + salesTax),
          valueSalesExcludingST: base,
          fixedNotifiedValueOrRetailPrice: 0,
          salesTaxApplicable: salesTax,
          salesTaxWithheldAtSource: 0,
          extraTax: '',
          furtherTax: 0,
          sroScheduleNo: '',                            // ENUM — SRO schedule
          fedPayable: 0,
          discount: n(ln.discount_amount),
          saleType: 'Goods at standard rate (default)', // ENUM — FBR sale type
          sroItemSerialNo: '',                          // ENUM — SRO serial
        };
      });
      return {
        invoiceType: 'Sale Invoice',
        invoiceDate: invoice?.issue_date || invoice?.date,   // YYYY-MM-DD
        sellerNTNCNIC: profile?.pk_ntn || '',
        sellerBusinessName: profile?.company_name || '',
        sellerProvince: profile?.pk_province || '',
        sellerAddress: profile?.address || '',
        buyerNTNCNIC: client?.tax_id || '',
        buyerBusinessName: client?.name || '',
        buyerProvince: client?.province || '',
        buyerAddress: client?.address || '',
        buyerRegistrationType: client?.tax_id ? 'Registered' : 'Unregistered',
        invoiceRefNo: invoice?.invoice_number || '',
        // scenarioId is REQUIRED in sandbox (drives which test scenario), ignored
        // in production. Default to the standard-rate goods scenario.
        scenarioId: invoice?.fbr_scenario_id || 'SN001',
        items,
      };
    },

    // Submit to FBR via the backend bridge. The backend attaches the seller's
    // decrypted Bearer token, routes to sandbox/production by profile.
    // fbr_environment, persists the returned IRN + QR on the invoice, and
    // returns them. Throws with FBR's validation message on rejection.
    async submit({ invoice, lines, profile, client }) {
      const payload = this.buildPayload({ invoice, lines, profile, client });
      const res = await fetch(`${API_BASE}/api/fbr/submit-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: profile?.id, invoice_id: invoice?.id, payload }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* non-JSON error body */ }
      if (!res.ok || data.status === 'error') {
        const err = new Error(data.detail || data.message || `FBR submission failed (HTTP ${res.status})`);
        err.code = data.code;   // not_configured | token_unreadable | token_rejected | unreachable | bad_response | rejected
        throw err;
      }
      return {
        fiscalNumber: data.invoiceNumber || null,
        qr: data.qr || null,
        status: data.status || 'submitted',
        raw: data.raw || data,
      };
    },
  },

  // Second Pakistani channel: PRA e-Invoicing for services supplied in Punjab.
  // Same contract as `einvoice`; the invoice page offers both buttons.
  einvoicePra: {
    enabled: true,
    featureKey: 'pra_invoicing',   // catalogue row for admin visibility only
    label: 'PRA e-Invoicing (Punjab)',

    // Build the PRA IMS request body. Lines carry a NET base in `total` (after
    // discount); tax is added per line. A credit note is a NEGATIVE copy of its
    // invoice (lib/creditNote.js): PRA takes it as InvoiceType 3 (Credit) with
    // positive figures and RefUSIN = the original invoice's number.
    buildPayload({ invoice, lines, profile, client }) {
      const n = (v) => Number(v || 0);
      const abs = (v) => Math.abs(n(v));
      const credit = n(invoice?.grand_total) < 0;
      const invoiceType = credit ? 3 : 1;
      const refUsin = credit ? String(invoice?.related?.invoice_number || invoice?.reference || '') : '';
      let totalSale = 0, totalTax = 0, totalDiscount = 0, totalQty = 0;
      const items = (lines || []).map((ln, i) => {
        const rate = n(ln.tax_rate ?? ln.taxRate);
        const qty = abs(ln.quantity);
        const base = abs(ln.total);                              // net, after discount
        const tax = round2(base * rate / 100);
        const discountPct = n(ln.discount);
        const discount = discountPct > 0 ? round2(qty * abs(ln.unit_price) * discountPct / 100) : 0;
        totalSale += base; totalTax += tax; totalDiscount += discount; totalQty += qty;
        return {
          ItemCode: String(ln.products?.barcode || ln.product_id || `LINE-${i + 1}`).slice(0, 50),
          ItemName: String(ln.description || ln.name || '').slice(0, 150),
          PCTCode: praPctCode(ln.hs_code),
          Quantity: qty,
          TaxRate: rate,
          SaleValue: base,
          Discount: discount,
          FurtherTax: 0,
          TaxCharged: tax,
          TotalAmount: round2(base + tax),
          InvoiceType: invoiceType,
          RefUSIN: refUsin,
        };
      });
      // The buyer id box holds an NTN/PNTN or a 13-digit CNIC.
      const buyerDigits = String(client?.tax_id || '').replace(/[\s-]/g, '');
      const buyerIsCnic = CNIC_RE.test(buyerDigits);
      return {
        InvoiceNumber: '',                                    // PRA fills this in
        POSID: n(profile?.pra_pos_id),                        // backend overwrites with the stored one
        USIN: invoice?.invoice_number || '',
        RefUSIN: refUsin,
        DateTime: praDateTime(invoice),
        BuyerName: String(client?.name || '').slice(0, 150),
        BuyerPNTN: buyerIsCnic ? '' : String(client?.tax_id || '').trim().slice(0, 9),
        BuyerCNIC: buyerIsCnic ? buyerDigits : '',
        BuyerPhoneNumber: String(client?.phone || '').slice(0, 20),
        TotalSaleValue: round2(totalSale),
        TotalTaxCharged: round2(totalTax),
        Discount: round2(totalDiscount),
        FurtherTax: 0,
        TotalBillAmount: round2(totalSale + totalTax),
        TotalQuantity: round2(totalQty),
        PaymentMode: praPaymentMode(invoice?.payment_method),
        InvoiceType: invoiceType,
        Items: items,
      };
    },

    // Submit via the backend bridge (POST /api/pra/submit-invoice), which adds
    // the seller's POS ID + decrypted token, routes by profile.pra_environment,
    // stores the returned PRA invoice number on the invoice and returns it.
    async submit({ invoice, lines, profile, client }) {
      const payload = this.buildPayload({ invoice, lines, profile, client });
      const res = await fetch(`${API_BASE}/api/pra/submit-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: profile?.id, invoice_id: invoice?.id, payload }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* non-JSON error body */ }
      if (!res.ok || data.status === 'error') {
        const err = new Error(data.detail || data.message || `PRA submission failed (HTTP ${res.status})`);
        err.code = data.code;   // not_configured | token_unreadable | token_rejected | unreachable | bad_response | rejected
        throw err;
      }
      return {
        fiscalNumber: data.invoiceNumber || null,
        qr: data.qr || null,
        verifyUrl: data.verifyUrl || praVerifyUrl(data.invoiceNumber),
        status: data.status || 'submitted',
        raw: data.raw || data,
      };
    },
  },
};
